<!-- Checkout Modal (shown when user clicks Get Started) -->
<app-checkout-modal
  [isOpen]="(showCheckout$ | async) ?? false"
  [product]="checkoutProduct$ | async"
  (closed)="onCheckoutClosed()"
  (goBack)="onCheckoutGoBack()"
  (success)="onPaymentSuccess($event)"
></app-checkout-modal>

@if ((isOpen$ | async) && !(showCheckout$ | async)) {
  <div
    class="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center sm:p-4 backdrop-blur-sm animate-fade-in"
    (click)="close()"
  >
    <div
      class="bg-gradient-to-br from-orange-50 via-white to-amber-50 rounded-t-2xl sm:rounded-3xl shadow-2xl w-full sm:max-w-3xl overflow-hidden max-h-[90vh] flex flex-col"
      (click)="$event.stopPropagation()"
    >
      <!-- Header -->
      <div class="px-4 pt-4 pb-2 sm:p-6 flex justify-between items-start shrink-0">
        <div class="flex-1 text-center pr-8">
          <h2 class="text-xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
            Choose your plan
          </h2>
          <p class="text-gray-500 mt-0.5 sm:mt-1 text-xs sm:text-base">
            Unlock unlimited exports and premium features
          </p>
        </div>
        <button
          (click)="close()"
          class="text-gray-400 hover:text-gray-600 cursor-pointer p-1.5 sm:p-2 hover:bg-gray-100 rounded-full transition-colors absolute right-3 top-3 sm:right-4 sm:top-4"
        >
          <lucide-icon [img]="icons.X" class="w-5 h-5 sm:w-6 sm:h-6"></lucide-icon>
        </button>
      </div>

      <!-- Content -->
      <div class="px-3 sm:px-6 pb-3 sm:pb-6 overflow-y-auto flex-1">
        @if (isLoading$ | async) {
          <div class="flex items-center justify-center py-8 sm:py-12">
            <lucide-icon
              [img]="icons.Loader2"
              class="w-8 h-8 text-brand animate-spin"
            ></lucide-icon>
          </div>
        } @else {
          <!-- Plans Grid -->
          <div class="flex flex-col sm:grid sm:grid-cols-3 gap-2 sm:gap-4 pt-2 sm:pt-4">
            @for (product of products$ | async; track product.id) {
              <button
                (click)="selectProduct(product)"
                class="relative flex flex-row sm:flex-col items-center sm:items-start p-3 sm:p-5 rounded-xl sm:rounded-2xl border-2 transition-all cursor-pointer text-left gap-3 sm:gap-0"
                [class.border-brand]="(selectedProductId$ | async) === product.id"
                [class.bg-brand/5]="(selectedProductId$ | async) === product.id"
                [class.shadow-lg]="(selectedProductId$ | async) === product.id"
                [class.border-gray-200]="(selectedProductId$ | async) !== product.id"
                [class.bg-white]="(selectedProductId$ | async) !== product.id"
                [class.hover:border-gray-300]="(selectedProductId$ | async) !== product.id"
                [class.hover:shadow-md]="(selectedProductId$ | async) !== product.id"
              >
                <!-- Badges (desktop only, positioned absolutely) -->
                @if (product.metadata.badge || product.metadata.secondaryBadge) {
                  <div class="hidden sm:flex absolute -top-3 left-4 gap-2">
                    @if (product.metadata.badge) {
                      <span
                        class="text-xs font-bold px-2.5 py-1 rounded-full shadow-sm"
                        [ngClass]="getBadgeClasses(product.metadata.badgeStyle)"
                      >
                        {{ product.metadata.badge }}
                      </span>
                    }
                  </div>
                }
                @if (product.metadata.secondaryBadge) {
                  <div
                    class="hidden sm:flex absolute -top-3 -right-2 items-center gap-1 bg-amber-400 text-gray-900 text-xs font-bold px-2.5 py-1 rounded-full shadow-md"
                  >
                    <lucide-icon [img]="icons.Rocket" class="w-3 h-3"></lucide-icon>
                    {{ product.metadata.secondaryBadge }}
                  </div>
                }

                <!-- Selection indicator -->
                <div
                  class="w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors sm:mb-3 sm:mt-1"
                  [class.border-brand]="(selectedProductId$ | async) === product.id"
                  [class.bg-brand]="(selectedProductId$ | async) === product.id"
                  [class.border-gray-300]="(selectedProductId$ | async) !== product.id"
                >
                  @if ((selectedProductId$ | async) === product.id) {
                    <lucide-icon
                      [img]="icons.Check"
                      class="w-3 h-3 sm:w-4 sm:h-4 text-white"
                    ></lucide-icon>
                  }
                </div>

                <!-- Plan info -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 sm:block">
                    <h3 class="font-bold text-gray-900 text-sm sm:text-base sm:mb-2">
                      {{ product.metadata.displayName }}
                    </h3>
                    <!-- Mobile badge inline -->
                    @if (product.metadata.badge) {
                      <span
                        class="sm:hidden text-[10px] font-bold px-2 py-0.5 rounded-full"
                        [ngClass]="getBadgeClasses(product.metadata.badgeStyle)"
                      >
                        {{ product.metadata.badge }}
                      </span>
                    }
                  </div>
                  <span class="text-[10px] sm:text-sm text-gray-500 block sm:mt-0">
                    {{ product.metadata.priceSubtext }}
                  </span>
                </div>

                <!-- Price -->
                <div class="text-right sm:text-left shrink-0">
                  <div class="flex items-baseline gap-1 sm:gap-2 sm:mb-1 justify-end sm:justify-start">
                    @if (product.metadata.originalPrice) {
                      <span class="text-gray-400 text-xs sm:text-sm line-through">
                        {{ product.metadata.originalPrice }}
                      </span>
                    }
                    <span class="text-xl sm:text-3xl font-extrabold text-brand">
                      {{ product.metadata.displayPrice }}
                    </span>
                  </div>
                </div>
              </button>
            }
          </div>

          <!-- CTA Button -->
          <button
            (click)="onGetStarted()"
            class="w-full mt-3 sm:mt-5 py-3 sm:py-4 px-6 bg-gradient-to-r from-brand to-brand-dark hover:from-brand-dark hover:to-brand text-white font-bold text-base sm:text-lg rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-all cursor-pointer transform hover:scale-[1.01]"
          >
            Get Started
          </button>

          <!-- Trust indicators -->
          <div class="text-center mt-2 sm:mt-4 space-y-1 sm:space-y-2">
            <div class="flex items-center justify-center gap-0.5 sm:gap-1">
              @for (i of [1, 2, 3, 4, 5]; track i) {
                <lucide-icon
                  [img]="icons.Star"
                  class="w-3.5 h-3.5 sm:w-5 sm:h-5 text-amber-400 fill-amber-400"
                ></lucide-icon>
              }
            </div>
            <p class="text-xs sm:text-sm font-medium text-gray-700">Easy plan cancellation</p>
          </div>

          <!-- Legal text -->
          @if (selectedProduct$ | async; as product) {
            <p class="text-[9px] sm:text-xs text-gray-400 text-center mt-2 sm:mt-4 px-2 leading-relaxed">
              @if (product.hasTrial && product.price.trialPeriod) {
                By continuing, you agree to start your {{ getTrialPeriodText(product) }} trial
                @if (product.price.trialAmount) {
                  for {{ formatPrice(product.price.trialAmount) }}.
                } @else {
                  for free.
                }
                If you don't cancel at least 24 hours prior to the end of the trial period,
                you will automatically be charged {{ formatPriceWithCurrency(product.price.amount, product.price.currency) }}
                every {{ product.price.interval }} until you cancel in the settings.
              } @else {
                By continuing, you agree that, if you don't cancel at least 24 hours prior to the end of the current period,
                you will automatically be charged {{ formatPriceWithCurrency(product.price.amount, product.price.currency) }}
                every {{ product.price.interval }} until you cancel in the settings.
              }
              Learn more about the cancellation and refund policy in the
              <a href="/subscription-terms" target="_blank" class="underline hover:text-gray-600"
              >Subscription Terms</a>.
            </p>
          }
        }
      </div>

      <!-- Safe area padding for mobile -->
      <div class="h-safe-area-inset-bottom sm:hidden"></div>
    </div>
  </div>
}
